import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { AuthContextValue, AuthState, CompanySelectionSource, UserCompany } from '@/features/auth/types/auth.types';
import { authService } from '@/features/auth/services/authService';
import { firebaseService } from '@/features/auth/services/firebaseService';
import { mapFirebaseError } from '@/features/auth/utils/mapFirebaseError';
import { tokenStorage } from '@/features/auth/utils/tokenStorage';
import { FirebaseError } from 'firebase/app';
import { decodeJwt } from '@/features/auth/utils/jwtUtils';
import { clear as openPanelClear, identify as openPanelIdentify, setGlobalProperties as openPanelSetGlobalProperties } from '@/shared/analytics/openpanel';
import { teamService } from '@/features/team/services/teamService/teamService';
import type { UserDetails } from '@/features/team/types/team.types';
import { companyStorage } from '@/features/auth/utils/companyStorage';
import { enabledCompanies, resolveActiveCompany } from '@/features/auth/utils/companies';
import { trackCompanyEvent } from '@/features/auth/analytics/companyEvents';
import { classifySessionChange } from '@/features/auth/utils/sessionSync';

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>(() => {
    const token = tokenStorage.getAccessTokenOptional();
    const hasToken = !!token;
    return {
      user: null,
      isAuthenticated: hasToken,
      isLoading: hasToken, // Set loading to true if we have a token that needs verification
      companies: enabledCompanies(companyStorage.getCompanies()),
      activeCompany: null,
    };
  });

  const [firebaseUser, setFirebaseUser] = useState<any>(null);
  const [dynamicPayload, setDynamicPayload] = useState<{ user_uuid?: string; customer_uuid?: string; customer_id?: string }>({});
  const [dynamicUser, setDynamicUser] = useState<{ email?: string; fullName?: string; role?: string; accessRole?: string }>({});
  const [member, setMember] = useState<UserDetails | null>(null);
  const [memberLoading, setMemberLoading] = useState(false);
  const [memberError, setMemberError] = useState<string | null>(null);

  const setPayload = useCallback((user_uuid: string, customer_uuid: string, customer_id?: string) => {
    setDynamicPayload({ user_uuid, customer_uuid, customer_id });
  }, []);

  const setUserData = useCallback((email: string, fullName: string, role: string, accessRole?: string) => {
    setDynamicUser({ email, fullName, role, accessRole });
  }, []);

  const setActiveCompany = useCallback((company: UserCompany) => {
    setState((prev) => ({ ...prev, activeCompany: company }));
  }, []);

  const resetSessionState = useCallback(() => {
    setMember(null);
    setMemberLoading(false);
    setMemberError(null);
    setDynamicUser({});
    setDynamicPayload({});
    setFirebaseUser(null);

    setState((prev) => ({
      ...prev,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      companies: [],
      activeCompany: null,
    }));

    openPanelClear();
  }, []);

  /**
   * Drops the session stored in this browser (tokens, companies and the user
   * in memory) without calling the BFF. Used before a new sign-in or when an
   * invitation opens, so a second account in the same browser never inherits
   * the previous account's session, data or role.
   */
  const clearLocalSession = useCallback(() => {
    tokenStorage.clearSession();
    companyStorage.clearCompanies();
    resetSessionState();
  }, [resetSessionState]);

  const applyCompanies = useCallback((list: UserCompany[] | undefined) => {
    if (!list) return;
    const enabled = enabledCompanies(list);
    companyStorage.setCompanies(enabled);
    setState((prev) => ({ ...prev, companies: enabled }));
  }, []);

  const checkAuth = useCallback(async (options?: { restoring?: boolean }): Promise<boolean> => {
    const token = tokenStorage.getAccessTokenOptional();
    if (token) {
      try {
        setMemberLoading(true);
        setMemberError(null);
        const decoded = decodeJwt<{ sub: string; customer_id?: string; firebase_uid?: string }>(token);
        const userUuid = decoded?.sub;
        if (!userUuid) {
          throw new Error('Missing user_uuid in access token');
        }

        const userData = await teamService.getMember(userUuid);
        if (tokenStorage.getAccessTokenOptional() !== token) {
          return false;
        }
        setMember(userData);

        setPayload(userData.uuid, userData.customer_uuid);
        setUserData(
          userData.email ?? '',
          `${userData.name ?? ''} ${userData.lname ?? ''}`.trim(),
          userData.role,
          userData.origin?.access_role
        );

        const user = {
          id: userData.uuid,
          customer_uuid: userData.customer_uuid,
          email: userData.email,
          firstName: userData.name,
          lastName: userData.lname,
          phone: userData.phone,
        };

        const profileId = decoded?.firebase_uid ?? userUuid;
        if (profileId) {
          openPanelIdentify({
            profileId,
            email: userData.email ?? undefined,
          });
        }
        const storedCompanies = enabledCompanies(companyStorage.getCompanies());
        const activeCompany = resolveActiveCompany(storedCompanies, decoded?.customer_id ?? userData.customer_uuid);
        openPanelSetGlobalProperties({
          customer_uuid: activeCompany?.customer_uuid ?? userData.customer_uuid,
          company_role: activeCompany?.role ?? userData.role,
          user_uuid: userData.uuid,
        });
        setState((prev) => ({
          ...prev,
          user,
          isAuthenticated: true,
          isLoading: false,
          companies: storedCompanies,
          activeCompany,
        }));
        if (activeCompany && userData.email) {
          companyStorage.setLastCompany(userData.email, activeCompany.customer_uuid);
        }
        if (options?.restoring && activeCompany) {
          trackCompanyEvent('company_restored', { customer_uuid: activeCompany.customer_uuid });
        }
        setMemberLoading(false);
        return true;
      } catch {
        tokenStorage.clearSession();
        companyStorage.clearCompanies();
        setMember(null);
        setMemberLoading(false);
        setMemberError('AUTH_CHECK_FAILED');
        setState((prev) => ({
          ...prev,
          user: null,
          isAuthenticated: false,
          isLoading: false,
          companies: [],
          activeCompany: null,
        }));
        return false;
      }
    } else {
      setState(prev => ({
        ...prev,
        user: null,
        isAuthenticated: false,
        isLoading: false
      }));
      setMember(null);
      setMemberLoading(false);
      setMemberError(null);
      return false;
    }
  }, [setPayload, setUserData]);

  useEffect(() => {
    const handleUnauthorized = (event: Event) => {
      const detail = event instanceof CustomEvent ? (event.detail as { hadSession?: boolean } | null) : null;
      const hadSession = detail?.hadSession ?? !!tokenStorage.getAccessTokenOptional();
      if (!hadSession) return;
      companyStorage.clearCompanies();
      setState((prev) => ({ ...prev, companies: [], activeCompany: null }));
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  useEffect(() => {
    const handleSessionChangedInOtherTab = (event: StorageEvent) => {
      if (event.storageArea !== localStorage || event.key !== tokenStorage.accessTokenKey) return;
      const change = classifySessionChange(event.oldValue, event.newValue);
      if (change === 'signed-out') {
        tokenStorage.clearTabSession();
        resetSessionState();
        return;
      }
      if (change === 'account-changed' || (change === 'signed-in' && !tokenStorage.hasPendingSignIn())) {
        tokenStorage.clearTabSession();
        window.location.reload();
      }
    };
    window.addEventListener('storage', handleSessionChangedInOtherTab);
    return () => window.removeEventListener('storage', handleSessionChangedInOtherTab);
  }, [resetSessionState]);

  useEffect(() => {
    const token = tokenStorage.getAccessTokenOptional();
    if (token) {
      checkAuth({ restoring: true });
    }
  }, []);

  const loginWithEmail = useCallback(async (email: string, password: string) => {
    clearLocalSession();
    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const { user, token } = await firebaseService.signIn(email, password);
      let dataUser =  {
        id: user.uid,
        email: user.email
      }
      
      setFirebaseUser(dataUser);

      tokenStorage.setFirebaseToken(token);
      const result = await preLogin();
      await checkAuth();
      return result;

    } catch (error) {
      if (error instanceof FirebaseError) {
        const apiError = mapFirebaseError(error);

        window.dispatchEvent(
          new CustomEvent('api:error', { detail: apiError })
        );

        throw apiError;
      }

      throw error;
    }
  }, [clearLocalSession]);

  const preLogin = useCallback(async () => {
    try {
      const { data } = await authService.preLogin();
      tokenStorage.setTempToken(data.temp_token);
      tokenStorage.setExpiresAt(data.expires_at);

      applyCompanies(data.companies ?? []);

      return data;
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, [applyCompanies]);

  const login = useCallback(async (targetCustomerUuid?: string, source: CompanySelectionSource = 'direct') => {
    try {
      const { data } = await authService.login(targetCustomerUuid);
        tokenStorage.setAccessToken(data.access_token);
        tokenStorage.setRefreshToken(data.refresh_token);
        tokenStorage.setAccessExpiresAt(data.expires_in);
        applyCompanies(data.companies);
        if (targetCustomerUuid) {
          trackCompanyEvent('company_selected', { customer_uuid: targetCustomerUuid, source });
        }

        // Extract user_uuid from JWT subject (sub)
        const decoded = decodeJwt<{ sub: string; customer_id?: string; firebase_uid?: string }>(data.access_token);
        if (decoded?.sub) {
          setPayload(decoded.sub, "");
        } else if (data.user_uuid) {
          // Fallback if it's already in the body for some reason
          setPayload(data.user_uuid, "");
        }

        const profileId = decoded?.firebase_uid ?? decoded?.sub ?? data.user_uuid ?? firebaseUser?.id;
        if (profileId) {
          openPanelIdentify({
            profileId,
            email: firebaseUser?.email ?? undefined,
          });
        }

        setState(prev => ({
          ...prev,
          isLoading: false,
          isAuthenticated: true,
          user: firebaseUser,
        }));

        await checkAuth();

        return true
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, [applyCompanies, checkAuth, firebaseUser, setPayload]);

  const switchCompany = useCallback(async (targetCustomerUuid: string) => {
    const data = await authService.switchCompany(targetCustomerUuid);
    applyCompanies(data.companies);
    const switched = await checkAuth();
    if (!switched) {
      throw new Error('SWITCH_COMPANY_AUTH_CHECK_FAILED');
    }
    trackCompanyEvent('company_switched', { customer_uuid: targetCustomerUuid });
  }, [applyCompanies, checkAuth]);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('BFF logout failed:', error);
    }

    clearLocalSession();

    try {
      await firebaseService.logout();
    } catch (error) {
      console.error('Firebase logout failed:', error);
    }

    await checkAuth();
  }, [checkAuth, clearLocalSession]);

  const refreshToken = useCallback(async () => {
    try {
      await authService.refresh();

      setState((prev) => ({
        ...prev,
        isAuthenticated: true,
      }));
    } catch {
      await logout();
    }
  }, [logout]);

  const value: AuthContextValue = {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    companies: state.companies,
    activeCompany: state.activeCompany,
    loginWithEmail,
    login,
    switchCompany,
    logout,
    clearLocalSession,
    refreshToken,
    member,
    memberLoading,
    memberError,
    payload: {
      user_uuid: dynamicPayload.user_uuid || state.user?.id,
      customer_uuid: dynamicPayload.customer_uuid || state.user?.customer_uuid,
      customer_id: dynamicPayload.customer_id,
    },
    userData: {
      email: dynamicUser.email || state.user?.email || firebaseUser?.email || undefined,
      fullName: dynamicUser.fullName || `${state.user?.firstName ?? ''} ${state.user?.lastName ?? ''}`.trim(),
      role: dynamicUser.role,
      accessRole: dynamicUser.accessRole,
    },
    setPayload,
    setUserData,
    setMember,
    setActiveCompany,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
